import type { NavigateOptions } from "react-router-dom";
import {
  useLocation,
  useMatch,
  useNavigate,
  useOutlet,
  useParams as useRouterParams,
  useRoutes,
  useSearchParams as useRouterSearchParams,
} from "react-router-dom";

/**
 * Drop-in replacement for the `next/navigation` hooks the storefront uses.
 *
 * Two behavioural differences from Next.js are worth knowing about, because
 * both were unavoidable when moving from the App Router to a SPA:
 *
 * 1. `useSearchParams()` returns the live `URLSearchParams` rather than a
 *    `ReadonlyURLSearchParams` snapshot. Reading it during render (which every
 *    caller does) behaves identically; navigating is done with `setSearchParams`.
 * 2. `redirect()` and `notFound()` throw, matching Next.js, so they can be
 *    called from anywhere in a component body or a route loader.
 */
export function useRouter() {
  const navigate = useNavigate();
  const [searchParams] = useRouterSearchParams();

  return {
    push: (to: string, options?: NavigateOptions) => navigate(`${to}`, options),
    replace: (to: string, options?: NavigateOptions) =>
      navigate(`${to}`, { ...options, replace: true }),
    refresh: () => navigate(0),
    back: () => navigate(-1),
    forward: () => navigate(1),
    // Vite bundles every route up front, so there is nothing to warm.
    prefetch: (_href: string) => {},
    // `useSearchParams()` is read here so the memo below invalidates with it.
    searchParams,
  };
}

export function usePathname(): string {
  return useLocation().pathname;
}

/** Next.js returns the params object itself — not a `[params, setParams]` tuple. */
export function useSearchParams(): URLSearchParams {
  return useRouterSearchParams()[0];
}

export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  return useRouterParams() as T;
}

export type RedirectError = Error & { __redirect: string; options?: NavigateOptions };

/** Mirrors `redirect()`: throws so it can be used inside a render body. */
export function redirect(url: string, options?: NavigateOptions): never {
  const error = new Error(`NEXT_REDIRECT;${url}`) as RedirectError;
  error.__redirect = url;
  error.options = options;
  throw error;
}

export type NotFoundError = Error & { __notFound: true };

/** Mirrors `notFound()` from the App Router. */
export function notFound(): never {
  const error = new Error("NEXT_NOT_FOUND") as NotFoundError;
  error.__notFound = true;
  throw error;
}

export function permanentRedirect(url: string): never {
  return redirect(url);
}

export { useLocation, useMatch, useNavigate, useOutlet, useRoutes };
